import Cookies from 'js-cookie'
import type { TokenData } from './types'

/**
 * Obtém o domínio para cookies compartilhados
 * Em desenvolvimento: .ecx.local
 * Em produção: .ecxpay.com.br
 */
export const getCookieDomain = (): string | undefined => {
  const hostname = window.location.hostname
  
  // Localhost ou IP - não define domain
  if (hostname === 'localhost' || hostname === '127.0.0.1' || /^\d+\.\d+\.\d+\.\d+$/.test(hostname)) {
    return undefined
  }
  
  // Desenvolvimento local (.ecx.local)
  if (hostname.endsWith('.ecx.local')) {
    return '.ecx.local'
  }
  
  // Produção (.ecxpay.com.br)
  if (hostname.endsWith('.ecxpay.com.br')) {
    return '.ecxpay.com.br'
  }
  
  // Outros domínios - pega o domínio raiz
  const parts = hostname.split('.')
  if (parts.length >= 2) {
    return '.' + parts.slice(-2).join('.')
  }
  
  return undefined
}

const getCookieOptions = (expiresInDays = 7): Cookies.CookieAttributes => ({
  domain: getCookieDomain(),
  secure: window.location.protocol === 'https:',
  sameSite: 'lax',
  expires: expiresInDays,
})

// ============================================
// Auth Cookie (Pay - email/token)
// ============================================

export interface AuthCookieData {
  email: string
  token: string
  temporaryToken?: string
  validationTokenSentAt?: string
}

const AUTH_COOKIE_NAME = 'ecx_auth'
const TEMP_AUTH_COOKIE_NAME = 'ecx_temp_auth'

export const authCookies = {
  /**
   * Salva a sessão completa (após 2FA validado)
   */
  setSession(email: string, token: string): void {
    const data: AuthCookieData = { email, token }
    Cookies.set(AUTH_COOKIE_NAME, JSON.stringify(data), getCookieOptions())
    this.clearTemporarySession()
  },

  /**
   * Retorna a sessão atual (se existir)
   */
  getSession(): AuthCookieData | null {
    const cookie = Cookies.get(AUTH_COOKIE_NAME)
    if (!cookie) return null
    
    try {
      return JSON.parse(cookie)
    } catch {
      return null
    }
  },

  /**
   * Retorna a sessão temporária (se existir)
   */
  getTemporarySession(): AuthCookieData | null {
    const cookie = Cookies.get(TEMP_AUTH_COOKIE_NAME)
    if (!cookie) return null
    
    try {
      return JSON.parse(cookie)
    } catch {
      return null
    }
  },

  /**
   * Verifica se há uma sessão válida
   */
  hasSession(): boolean {
    const session = this.getSession()
    return !!(session?.email && session?.token)
  },

  /**
   * Limpa a sessão completa
   */
  clearSession(): void {
    Cookies.remove(AUTH_COOKIE_NAME, { domain: getCookieDomain() })
  },

  /**
   * Limpa a sessão temporária
   */
  clearTemporarySession(): void {
    Cookies.remove(TEMP_AUTH_COOKIE_NAME, { domain: getCookieDomain() })
  },

  /**
   * Limpa todas as sessões (logout)
   */
  clearAll(): void {
    this.clearSession()
    this.clearTemporarySession()
  },

  /**
   * Obtém email e token para headers
   */
  getCredentials(): { email: string; token: string } | null {
    const session = this.getSession()
    if (session?.email && session?.token) {
      return { email: session.email, token: session.token }
    }
    return null
  },
}

// ============================================
// Corp Auth Cookie (accessToken/refreshToken)
// ============================================

export interface CorpAuthCookieData {
  accessToken: string
  refreshToken: string
  accountId: string | null
  businessCnpj: string | null
  expiresAt: string // ISO string
}

const CORP_AUTH_COOKIE_NAME = 'ecx_corp_auth'

export const corpAuthCookies = {
  /**
   * Salva a sessão Corp
   */
  setSession(data: CorpAuthCookieData): void {
    Cookies.set(CORP_AUTH_COOKIE_NAME, JSON.stringify(data), getCookieOptions())
  },

  /**
   * Atualiza tokens (após refresh)
   */
  updateTokens(tokens: TokenData): void {
    const expiresAt = tokens.expiresAt instanceof Date 
      ? tokens.expiresAt.toISOString() 
      : tokens.expiresAt

    const data: CorpAuthCookieData = {
      accessToken: tokens.accessToken,
      refreshToken: tokens.refreshToken,
      accountId: tokens.accountId || null,
      businessCnpj: tokens.businessCnpj ?? null,
      expiresAt,
    }
    
    Cookies.set(CORP_AUTH_COOKIE_NAME, JSON.stringify(data), getCookieOptions())
  },

  /**
   * Retorna a sessão atual
   */
  getSession(): CorpAuthCookieData | null {
    const cookie = Cookies.get(CORP_AUTH_COOKIE_NAME)
    if (!cookie) return null
    
    try {
      return JSON.parse(cookie)
    } catch {
      return null
    }
  },

  /**
   * Retorna os dados como TokenData
   */
  getTokenData(): TokenData | null {
    const session = this.getSession()
    if (!session) return null

    return {
      accessToken: session.accessToken,
      refreshToken: session.refreshToken,
      expiresAt: new Date(session.expiresAt),
      accountId: session.accountId,
      businessCnpj: session.businessCnpj ?? null,
    }
  },

  /**
   * Verifica se há sessão válida (não expirada)
   */
  hasValidSession(): boolean {
    const session = this.getSession()
    if (!session?.accessToken || !session?.expiresAt) return false
    
    return new Date(session.expiresAt) > new Date()
  },

  /**
   * Verifica se o token está próximo de expirar (5 minutos)
   */
  isExpiringSoon(thresholdSeconds = 300): boolean {
    const session = this.getSession()
    if (!session?.expiresAt) return true
    
    const expiresAt = new Date(session.expiresAt)
    const now = new Date()
    const timeUntilExpiry = (expiresAt.getTime() - now.getTime()) / 1000
    
    return timeUntilExpiry <= thresholdSeconds
  },

  /**
   * Limpa a sessão
   */
  clearSession(): void {
    Cookies.remove(CORP_AUTH_COOKIE_NAME, { domain: getCookieDomain() })
  },

  /**
   * Retorna o access token
   */
  getAccessToken(): string | null {
    return this.getSession()?.accessToken || null
  },

  /**
   * Retorna o refresh token
   */
  getRefreshToken(): string | null {
    return this.getSession()?.refreshToken || null
  },

  /**
   * Retorna o account ID
   */
  getAccountId(): string | null {
    return this.getSession()?.accountId || null
  },
}

// ============================================
// Generic Cookie Helper
// ============================================

/**
 * Helper genérico para criar gerenciadores de cookie
 */
export function createCookieManager<T extends Record<string, unknown>>(cookieName: string) {
  return {
    set(data: T, expiresInDays = 7): void {
      Cookies.set(cookieName, JSON.stringify(data), getCookieOptions(expiresInDays))
    },

    get(): T | null {
      const cookie = Cookies.get(cookieName)
      if (!cookie) return null
      
      try {
        return JSON.parse(cookie)
      } catch {
        return null
      }
    },

    clear(): void {
      Cookies.remove(cookieName, { domain: getCookieDomain() })
    },

    exists(): boolean {
      return !!Cookies.get(cookieName)
    },
  }
}
